import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TalkToEngineerModal } from "@/components/TalkToEngineerModal";
import { ArrowRight, Cpu, MapPin, Sliders, FileText, Factory, Pickaxe, Zap, Radio, ShieldCheck, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { setPlausibleDomain } from "@/lib/analytics";
import { useT } from "@/lib/i18n";
import { sectorName } from "@/lib/resultsFr";
import { money, moneyK, perYr } from "@/lib/numfmt";

const SECTORS = [
  { icon: Factory, name: "Industrial", desc: "landing.sectors.industrial" },
  { icon: Pickaxe, name: "Mining", desc: "landing.sectors.mining" },
  { icon: Zap, name: "Utilities", desc: "landing.sectors.utilities" },
  { icon: Radio, name: "Telecom", desc: "landing.sectors.telecom" },
];

// Bundle cards keep their slots (and height) while /config loads; prices stay "—" until it answers.
const BUNDLES = [
  { credits: 3, testid: "pricing-bundle-3" },
  { credits: 5, testid: "pricing-bundle-5", highlight: true },
  { credits: 10, testid: "pricing-bundle-10" },
  { credits: 20, testid: "pricing-bundle-20" },
];

export const Landing = () => {
  const t = useT();
  const [talkOpen, setTalkOpen] = useState(false);
  const [enterpriseTalk, setEnterpriseTalk] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => {
      setPricing({ report: r.data.report_price_usd, tiers: r.data.pricing_tiers || [] });
      if (r.data.plausible_domain) setPlausibleDomain(r.data.plausible_domain);
    }).catch(() => {});
  }, []);

  const tierFor = (n) => pricing?.tiers.find((x) => x.package_id === `credit_bundle_${n}`);

  return (
    <div className="App">
      <Header />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: "var(--hairline)", background: "var(--white)" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="pill" style={{ background: "var(--paper)", color: "var(--brand-green)" }}>
              <ShieldCheck size={12} /> {t("landing.hero.tag")}
            </span>
            <h1 className="mt-6 font-display font-bold tracking-tight leading-[1.02]" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
              {t("landing.hero.title_1")}<br />{t("landing.hero.title_2")} <span style={{ color: "var(--brand-green)" }}>{t("landing.hero.title_3")}</span>
            </h1>
            <p className="mt-6 text-lg" style={{ color: "var(--body)", maxWidth: 620 }}>
              {t("landing.hero.subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/configurator" className="btn-primary" data-testid="hero-start">
                {t("landing.hero.start")} <ArrowRight size={16} />
              </Link>
              <button onClick={() => setTalkOpen(true)} className="btn-secondary" data-testid="hero-talk">
                {t("landing.hero.talk")}
              </button>
              <Link
                to="/pricing"
                className="text-sm font-medium inline-flex items-center gap-1 self-center underline underline-offset-4"
                style={{ color: "var(--brand-green)" }}
                data-testid="hero-see-pricing"
              >
                {t("landing.hero.see_pricing")} <ArrowRight size={12} />
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm" style={{ color: "var(--muted)" }} data-testid="hero-bullets">
              <span>{t("landing.hero.countries")}</span>
              <span>{t("landing.hero.west_africa")}</span>
              <span>{t("landing.hero.site_prefilled")}</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card p-6" style={{ background: "var(--paper)" }}>
              <div className="overline mb-3">{t("landing.sample.overline")}</div>
              <div className="grid grid-cols-2 gap-3">
                <SummaryTile k={t("landing.sample.mix")} v={t("landing.sample.mix_v")} />
                <SummaryTile k={t("landing.sample.firm")} v={t("landing.sample.firm_v")} />
                <SummaryTile k={t("landing.sample.renewable")} v={t("landing.sample.renewable_v")} />
                <SummaryTile k={t("landing.sample.capex")} v={t("landing.sample.capex_v")} />
                <SummaryTile k="LCOE" v={t("landing.sample.lcoe_v")} />
              </div>
              <div className="mt-5 pt-4 border-t text-xs" style={{ borderColor: "var(--hairline)", color: "var(--muted)" }}>
                {t("landing.sample.note")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section id="methodology" style={{ background: "var(--paper)", borderBottom: "1px solid var(--hairline)" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-2xl">
            <div className="overline mb-3">{t("landing.method.overline")}</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">{t("landing.method.title_1")}</h2>
            <p className="mt-4 text-base" style={{ color: "var(--body)" }} data-testid="method-body">
              {t("landing.method.body")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
            <Step icon={Sliders} n="01" t={t("landing.method.s1.t")} d={t("landing.method.s1.d")} />
            <Step icon={MapPin} n="02" t={t("landing.method.s2.t")} d={t("landing.method.s2.d")} />
            <Step icon={Cpu} n="03" t={t("landing.method.s3.t")} d={t("landing.method.s3.d")} />
            <Step icon={FileText} n="04" t={t("landing.method.s4.t")} d={t("landing.method.s4.d")} />
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section id="sectors" style={{ borderBottom: "1px solid var(--hairline)" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <div className="overline mb-3">{t("landing.sectors.overline")}</div>
              <h2 className="font-display text-3xl md:text-4xl">{t("landing.sectors.title_1")}</h2>
            </div>
            <p style={{ color: "var(--muted)", maxWidth: 460 }}>{t("landing.sectors.desc")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECTORS.map((s) => (
              <div key={s.name} className="card p-6" data-testid={`sector-${s.name.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-md flex items-center justify-center mb-4" style={{ background: "var(--paper)" }}>
                  <s.icon size={22} style={{ color: "var(--brand-green)" }} />
                </div>
                <div className="font-display text-lg">{sectorName(s.name)}</div>
                <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{t(s.desc)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ background: "var(--paper)", borderBottom: "1px solid var(--hairline)" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="mb-10 max-w-3xl">
            <div className="overline mb-3">{t("landing.pricing.overline")}</div>
            <h2 className="font-display text-3xl md:text-4xl">{t("landing.pricing.title")}</h2>
            <p className="mt-4 text-sm" style={{ color: "var(--muted)" }}>{t("landing.pricing.desc")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pricing-grid">
            {/* First pass — free */}
            <div className="card p-6" data-testid="pricing-first-pass">
              <div className="overline">{t("landing.pricing.first.overline")}</div>
              <div className="mt-2 font-display text-4xl">{t("landing.pricing.first.price")}</div>
              <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{t("landing.pricing.first.desc")}</div>
              <Bullets keys={["first.b1", "first.b2", "first.b3", "first.b4"]} t={t} />
              <Link to="/configurator" className="btn-secondary w-full justify-center mt-6" data-testid="pricing-first-pass-cta">{t("nav.start_cta")}</Link>
            </div>

            {/* Detailed report — single */}
            <div className="card p-6" style={{ borderColor: "var(--brand-green)" }} data-testid="pricing-single">
              <div className="flex items-center justify-between">
                <div className="overline">{t("landing.pricing.single.overline")}</div>
                <span className="pill" style={{ background: "var(--brand-green)", color: "var(--white)" }}>{t("landing.pricing.single.badge")}</span>
              </div>
              <div className="mt-2 font-display text-4xl" data-testid="pricing-single-price">{pricing ? money(pricing.report) : "—"}</div>
              <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{t("landing.pricing.single.desc")}</div>
              <Bullets keys={["single.b1", "single.b2", "single.b3", "single.b4", "single.b5"]} t={t} />
              <Link to="/configurator" className="btn-primary w-full justify-center mt-6" data-testid="pricing-single-cta">{t("landing.pricing.single.cta")}</Link>
            </div>

            {BUNDLES.map((b) => (
              <BundleCard key={b.credits} {...b} tier={tierFor(b.credits)} reportPrice={pricing?.report} t={t} />
            ))}

            {/* Custom / Enterprise */}
            <div className="card p-6" style={{ background: "var(--charcoal)", color: "var(--white)" }} data-testid="pricing-enterprise">
              <div className="overline" style={{ color: "var(--brand-bright)" }}>{t("landing.pricing.ent.overline")}</div>
              <div className="mt-2 font-display text-4xl" style={{ color: "var(--white)" }}>
                {moneyK(3000)}–{moneyK(15000)}<span className="text-lg opacity-70">{perYr()}</span>
              </div>
              <div className="text-sm mt-1 opacity-80">{t("landing.pricing.ent.desc")}</div>
              <Bullets keys={["ent.b1", "ent.b2", "ent.b3", "ent.b4"]} t={t} className="opacity-90" />
              <button onClick={() => { setEnterpriseTalk(true); }} className="btn-secondary w-full justify-center mt-6" style={{ background: "var(--white)", color: "var(--charcoal)" }} data-testid="pricing-enterprise-cta">{t("landing.pricing.ent.cta")}</button>
            </div>

            {/* Extended workflow (kept off Paddle's automated pricing grid — engagement fees are handled outside the app) */}
            <div className="card p-6" data-testid="pricing-consultancy">
              <div className="overline">{t("landing.pricing.ext.overline")}</div>
              <div className="mt-2 font-display text-3xl">{t("landing.pricing.ext.price")}</div>
              <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>{t("landing.pricing.ext.desc")}</div>
              <Bullets keys={["ext.b1", "ext.b2", "ext.b3"]} t={t} />
              <button onClick={() => setTalkOpen(true)} className="btn-secondary w-full justify-center mt-6" data-testid="pricing-consultancy-cta">{t("landing.hero.talk")}</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
          <div className="card p-10 md:p-14" style={{ background: "var(--charcoal)", color: "var(--white)" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="overline" style={{ color: "var(--brand-bright)" }}>Power Configurator</div>
                <h3 className="font-display text-3xl md:text-4xl mt-2" style={{ color: "var(--white)" }}>{t("landing.cta.title")}</h3>
                <p className="mt-3 text-sm opacity-80">{t("landing.cta.body")}</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link to="/configurator" className="btn-primary" style={{ background: "var(--brand-bright)", borderColor: "var(--brand-bright)", color: "var(--charcoal)" }} data-testid="cta-configure-primary">
                  {t("nav.start_cta")} <TrendingUp size={16} />
                </Link>
                <button onClick={() => setTalkOpen(true)} className="btn-secondary" style={{ background: "transparent", color: "var(--white)", borderColor: "rgba(255,255,255,0.3)" }} data-testid="cta-contact">{t("landing.hero.talk")}</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <TalkToEngineerModal open={talkOpen} onClose={() => setTalkOpen(false)} source="landing" />
      <TalkToEngineerModal open={enterpriseTalk} onClose={() => setEnterpriseTalk(false)} source="enterprise" />
    </div>
  );
};

const SummaryTile = ({ k, v }) => (
  <div style={{ background: "var(--white)", border: "1px solid var(--hairline)", borderRadius: 8, padding: 12 }}>
    <div className="overline" style={{ fontSize: "0.62rem" }}>{k}</div>
    <div className="font-display text-lg mt-1" style={{ color: "var(--charcoal)" }}>{v}</div>
  </div>
);

const Bullets = ({ keys, t, className = "" }) => (
  <ul className={`mt-4 text-sm space-y-1.5 ${className}`} style={className ? undefined : { color: "var(--body)" }}>
    {keys.map((k) => <li key={k}>{t(`landing.pricing.${k}`)}</li>)}
  </ul>
);

// Saving = credits × the live single-report price − the bundle price; none until /config has answered.
export const bundleSaving = (tier, reportPrice) =>
  tier && reportPrice > 0 ? Math.max(0, reportPrice * tier.credits - tier.price_usd) : 0;

const BundleCard = ({ credits, testid, highlight, tier, reportPrice, t }) => {
  const savings = bundleSaving(tier, reportPrice);
  const fill = (k, vars) => Object.entries(vars).reduce((s, [a, b]) => s.replace(`{${a}}`, b), t(k));
  return (
    <div className="card p-6" style={highlight ? { borderColor: "var(--brand-green)" } : undefined} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="overline">{fill("landing.pricing.bundle.overline", { n: credits })}</div>
        {highlight && (
          <span className="pill" style={{ background: "var(--brand-green)", color: "var(--white)" }}>{t("landing.pricing.bundle.badge")}</span>
        )}
      </div>
      <div className="mt-2 font-display text-4xl" data-testid={`${testid}-price`}>{tier ? money(tier.price_usd) : "—"}</div>
      <div className="text-sm mt-1" style={{ color: "var(--muted)" }} data-testid={`${testid}-per-report`}>
        {tier ? money(tier.per_report_usd) : "—"}{t("landing.pricing.bundle.per_report")}
        {savings > 0 ? <span data-testid={`${testid}-saving`}>{fill("landing.pricing.bundle.save", { amount: money(savings) })}</span> : null}
      </div>
      <ul className="mt-4 text-sm space-y-1.5" style={{ color: "var(--body)" }}>
        <li>{fill("landing.pricing.bundle.b1", { n: credits })}</li>
        <li>{t("landing.pricing.bundle.b2")}</li>
        <li>{t("landing.pricing.bundle.b3")}</li>
        <li>{t("landing.pricing.bundle.b4")}</li>
      </ul>
      <Link to="/configurator" className={highlight ? "btn-primary w-full justify-center mt-6" : "btn-secondary w-full justify-center mt-6"} data-testid={`${testid}-cta`}>
        {t("landing.pricing.bundle.cta")}
      </Link>
    </div>
  );
};

const Step = ({ icon: Icon, n, t, d }) => (
  <div className="card p-6" style={{ background: "var(--white)" }}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--paper)" }}>
        <Icon size={20} style={{ color: "var(--brand-green)" }} />
      </div>
      <span className="font-mono text-xs" style={{ color: "var(--muted)" }}>{n} / 04</span>
    </div>
    <div className="font-display text-lg mt-4">{t}</div>
    <div className="text-sm mt-2" style={{ color: "var(--muted)" }}>{d}</div>
  </div>
);
